<template>
  <PageLayout title="Devices">
    <template #actions>
      <a :href="macZip" download class="btn btn-outline-secondary btn-sm mr-2">
        <i class="fas fa-download mr-1"></i> macOS
      </a>
      <a :href="winZip" download class="btn btn-outline-secondary btn-sm mr-2">
        <i class="fas fa-download mr-1"></i> Windows
      </a>
      <router-link to="/devices/pair" class="btn btn-primary btn-sm">
        <i class="fas fa-link mr-1"></i> Pair Device
      </router-link>
    </template>

    <ContentCard>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <p class="text-muted mb-0">Connector nodes paired to this account.</p>
        <span :class="wsConnected ? 'text-success' : 'text-danger'" class="small">
          <i class="fas fa-circle" style="font-size:0.4rem"></i> Live
        </span>
      </div>

      <div v-if="!nodes.length" class="text-center text-muted py-5">
        <i class="fas fa-server fa-2x mb-3 d-block"></i>
        <p>No devices paired yet.</p>
        <router-link to="/devices/pair" class="btn btn-outline-primary btn-sm">Pair your first device</router-link>
      </div>

      <div v-else class="table-responsive">
        <table class="table table-hover mb-0">
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>GO</th>
              <th>Adapters</th>
              <th class="text-right" style="width:180px">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="node in nodes" :key="node.id">
              <td>
                <i class="fas fa-circle mr-2" :class="node.online ? 'text-success' : 'text-secondary'" style="font-size:0.4rem"></i>
                <strong>{{ node.name }}</strong>
                <div class="text-muted small text-monospace">{{ shortId(node.id) }}</div>
              </td>
              <td>
                <span class="badge" :class="node.online ? 'badge-success' : 'badge-secondary'">
                  {{ node.online ? 'Online' : 'Offline' }}
                </span>
              </td>
              <td>
                <span v-if="node.ready_status" class="badge badge-danger">
                  <i class="fas fa-rocket mr-1"></i>GO
                </span>
                <span v-else class="text-muted">—</span>
              </td>
              <td>
                <span v-if="!node.devices || !node.devices.length" class="text-muted small">—</span>
                <span
                  v-for="d in node.devices"
                  :key="d.id"
                  class="badge badge-light border mr-1"
                  :title="d.enabled ? 'enabled' : 'disabled'"
                >
                  {{ d.adapter }}
                </span>
              </td>
              <td class="text-right text-nowrap">
                <button
                  class="btn btn-sm btn-outline-warning mr-1"
                  title="Reset connection (fix command timeout / stuck adapter)"
                  :disabled="resettingId === node.id"
                  @click="resetDevice(node)"
                >
                  <i class="fas" :class="resettingId === node.id ? 'fa-spinner fa-spin' : 'fa-redo'"></i>
                </button>
                <button class="btn btn-sm btn-outline-secondary mr-1" title="Rename" @click="openEdit(node)">
                  <i class="fas fa-pen"></i>
                </button>
                <button class="btn btn-sm btn-outline-secondary mr-1" title="Copy ID" @click="copyId(node.id)">
                  <i class="fas fa-copy"></i>
                </button>
                <button class="btn btn-sm btn-outline-danger" title="Delete" @click="confirmDelete(node)">
                  <i class="fas fa-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </ContentCard>

    <modal name="edit-device" height="auto" :width="420" :adaptive="true">
      <div class="p-4">
        <h5 class="mb-3">Rename Device</h5>
        <div class="form-group">
          <label class="small text-muted">Name</label>
          <input
            v-model="editForm.name"
            class="form-control"
            maxlength="100"
            @keyup.enter="saveEdit"
          >
        </div>
        <div class="form-group mb-3">
          <label class="small text-muted">Node ID</label>
          <input :value="editForm.id" class="form-control form-control-sm text-monospace" readonly>
        </div>
        <div class="d-flex justify-content-end">
          <button class="btn btn-light mr-2" @click="$modal.hide('edit-device')">Cancel</button>
          <button class="btn btn-primary" :disabled="!editForm.name.trim() || saving" @click="saveEdit">
            {{ saving ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </div>
    </modal>

    <modal name="delete-device" height="auto" :width="400" :adaptive="true">
      <div class="p-4">
        <h5 class="mb-2">Delete Device</h5>
        <p class="text-muted mb-3">
          Hapus <strong>{{ deleteTarget && deleteTarget.name }}</strong>? Connector harus pair ulang.
        </p>
        <div class="d-flex justify-content-end">
          <button class="btn btn-light mr-2" @click="$modal.hide('delete-device')">Cancel</button>
          <button class="btn btn-danger" :disabled="deleting" @click="doDelete">
            {{ deleting ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </div>
    </modal>
  </PageLayout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { invalidateQlcButtons } from '@/widgets/qlcCache'
import { getConnectorDownloads } from '@/api/connector'

export default {
  data() {
    return {
      editForm: { id: '', name: '' },
      deleteTarget: null,
      saving: false,
      deleting: false,
      resettingId: '',
      pollNodes: null,
      macZip: `${process.env.BASE_URL}ControlLabs-Connector-macOS.zip`,
      winZip: `${process.env.BASE_URL}ControlLabs-Connector-Windows.zip`,
    }
  },
  computed: {
    ...mapGetters('mission', ['nodes', 'wsConnected']),
  },
  async mounted() {
    this.loadDownloadUrls()
    await this.fetchNodes()
    this.connectWS()
    this.pollNodes = setInterval(() => this.fetchNodes(), 10000)
  },
  beforeDestroy() {
    this.disconnectWS()
    clearInterval(this.pollNodes)
  },
  methods: {
    ...mapActions('mission', ['fetchNodes', 'connectWS', 'disconnectWS', 'updateNode', 'deleteNode', 'resetNode']),
    async loadDownloadUrls() {
      try {
        const { data } = await getConnectorDownloads()
        const info = data?.data
        if (info?.macosUrl) this.macZip = info.macosUrl
        if (info?.windowsUrl) this.winZip = info.windowsUrl
      } catch (e) {
        // bundled zip fallback in data()
      }
    },
    shortId(id) {
      return id ? `${id.slice(0, 8)}…` : ''
    },
    async resetDevice(node) {
      if (!node || this.resettingId) return
      this.resettingId = node.id
      try {
        await this.resetNode(node.id)
        invalidateQlcButtons(node.id)
        this.$toast.success(`Reset ${node.name} — menunggu reconnect…`)
        setTimeout(() => this.fetchNodes(), 2000)
      } catch (e) {
        this.$toast.error(e.response?.data?.message || e.message || 'Reset failed')
      } finally {
        this.resettingId = ''
      }
    },
    openEdit(node) {
      this.editForm = { id: node.id, name: node.name }
      this.$modal.show('edit-device')
    },
    async saveEdit() {
      const name = this.editForm.name.trim()
      if (!name) return
      this.saving = true
      try {
        await this.updateNode({ id: this.editForm.id, name })
        this.$toast.success('Device renamed')
        this.$modal.hide('edit-device')
      } catch (e) {
        this.$toast.error(e.response?.data?.message || 'Failed to rename')
      } finally {
        this.saving = false
      }
    },
    confirmDelete(node) {
      this.deleteTarget = node
      this.$modal.show('delete-device')
    },
    async doDelete() {
      if (!this.deleteTarget) return
      this.deleting = true
      try {
        await this.deleteNode(this.deleteTarget.id)
        this.$toast.success('Device deleted')
        this.$modal.hide('delete-device')
        this.deleteTarget = null
      } catch (e) {
        this.$toast.error(e.response?.data?.message || 'Failed to delete')
      } finally {
        this.deleting = false
      }
    },
    async copyId(id) {
      try {
        await navigator.clipboard.writeText(id)
        this.$toast.success('Node ID copied')
      } catch (e) {
        this.$toast.error('Copy failed')
      }
    },
  },
}
</script>
